import { Form, Formik } from 'formik';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Address, Party } from '../../../../@posturinn/client-react';
import { closeAllModals } from '../../../../actions/modalActions';
import { createRecipient, editRecipient } from '../../../../actions/registerShipmentActions';
import { getCountry } from '../../../../helpers/apiHelpers/registerShipmentHelpers';
import { normalizeShipmentPhoneForDestination } from '../../../../helpers/formHelpers/formHelpers';
import { filterCountries } from '../../../../helpers/select/filterCountries';
import { getCurrentLangPretty } from '../../../../helpers/url/urlHelpers';
import useCountries from '../../../../hooks/useCountries';
import usePostCodes from '../../../../hooks/usePostCodes';
import useRecipients from '../../../../hooks/useRecipients';
import { ReactComponent as Close } from '../../../../icons/close-button.svg';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import FormikSelect from '../../../base/forms/formikSelect';
import { StyledFormikFastField } from '../../../base/forms/input';
import { Column, Columns } from '../../../base/grid';
import { A } from '../../../base/typography';
import { RawLoader } from '../../../loading/loading';
import * as StyledStepType from '../../../registerShipment/registerShipmentForm/stepType/stepType.styles';
import * as StyledModals from '../myShipmentsModals.styles';
import { recipientInitialValues, recipientSchema } from './recipient.schema';

interface Props {}

const RecipientModal: FC<Props> = () => {
	const dispatch = useDispatch<any>();
	const { t } = useTranslation(['registerShipment', 'myShipments']);
	const { countries, countriesLoading } = useCountries(getCurrentLangPretty(), true);
	const { postCodes, postCodesLoading } = usePostCodes();
	const { readOnlyValuesMapper } = useSelector(({ registerShipmentReducer }) => registerShipmentReducer);
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile } = userProfileReducer;
	const { recipients, recipientsLoading } = useRecipients(userProfile.userId);
	let initialized = false;

	const closeModal = () => {
		dispatch(closeAllModals());
		document.body.style.overflow = 'unset';
	};
	document.body.style.overflow = 'hidden';

	const { modalHeading, modalQrCodeValue, modalSubmitError, modalSubmitErrorDetails, modalSubmitPending } =
		useSelector(({ modalReducer }) => modalReducer);

	if (modalQrCodeValue && userProfile && countries && !initialized && !countriesLoading) {
		const recipient: Party = recipients.find(recipient => recipient.id === modalQrCodeValue);

		recipientInitialValues.country = getCountry(recipient.address?.country, countries);
		recipientInitialValues.receiverName = recipient.name;
		recipientInitialValues.receiverSsn = recipient.identificationNumber;
		recipientInitialValues.receiverEmail = recipient.email;
		recipientInitialValues.receiverPhone = recipient.phone;
		recipientInitialValues.receiverAddress = recipient.address?.street1;

		if (recipient.address?.country === 'IS') {
			recipientInitialValues.receiverPostCode = {
				value: recipient.address?.postalCode,
				label: recipient.address?.postalCode + ' ' + recipient.address?.city,
			};
		} else {
			recipientInitialValues.receiverForeignAddressExtended = recipient.address?.street2;
			recipientInitialValues.receiverForeignPostCode = recipient.address?.postalCode;
			recipientInitialValues.receiverForeignPlace = recipient.address?.city;
		}

		initialized = true;
	} else if (!initialized) {
		recipientInitialValues.country = { value: 'IS', label: getCurrentLangPretty() === 'is' ? 'Ísland' : 'Iceland' };
		recipientInitialValues.receiverSsn = '';
		recipientInitialValues.receiverName = '';
		recipientInitialValues.receiverAddress = '';
		recipientInitialValues.receiverPostCode = null;
		recipientInitialValues.receiverPhone = '';
		recipientInitialValues.receiverEmail = '';
		recipientInitialValues.receiverForeignPostCode = '';
		recipientInitialValues.receiverForeignAddressExtended = '';
		recipientInitialValues.receiverForeignPlace = '';

		initialized = true;
	}

	const handleSubmit = values => {
		const recipient: Party = new Party();
		const address: Address = new Address();

		recipient.name = values.receiverName;
		recipient.identificationNumber = values.receiverSsn;
		recipient.email = values.receiverEmail;
		recipient.phone = normalizeShipmentPhoneForDestination(values.receiverPhone, values.country.value);
		address.country = values.country.value;

		if (values.country.value === 'IS') {
			address.street1 = values.receiverAddress;
			address.street2 = '';
			address.postalCode = values.receiverPostCode.label.substring(0, 3) || '';
			address.city = values.receiverPostCode.label.substring(4) || '';
		} else {
			address.street2 = values.receiverForeignAddressExtended;
			address.street1 = values.receiverAddress;
			address.postalCode = values.receiverForeignPostCode;
			address.city = values.receiverForeignPlace;
		}

		recipient.address = address;

		if (modalQrCodeValue) {
			recipient.id = modalQrCodeValue;
			dispatch(editRecipient(userProfile.userId, recipient, recipients));
		} else dispatch(createRecipient(userProfile.userId, recipient, recipients));
	};

	return (
		<StyledModals.Wrapper>
			<StyledModals.Heading>
				<div>{modalHeading}</div>
				<A onClick={closeModal}>
					<Close />
				</A>
			</StyledModals.Heading>
			{modalSubmitError && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					<div>{modalSubmitErrorDetails || t('myShipments:genericError')}</div>
				</StyledModals.ErrorMessage>
			)}
			{recipientsLoading || countriesLoading || postCodesLoading ? (
				<RawLoader />
			) : (
				<Formik
					initialValues={recipientInitialValues}
					validationSchema={recipientSchema}
					onSubmit={handleSubmit}>
					{({ values, setFieldTouched, setFieldValue }) => (
						<StyledModals.ModalForm>
							<Form>
								<Columns>
									<Column>
										<FormikFieldWrapper
											label={`${t('step-type.where-are-you-delivering-to')}`}
											name="country"
											smallLabel
											languageComponent="registerShipment">
											<FormikSelect
												id="country"
												name="country"
												value={values.country}
												options={countries}
												onBlur={setFieldTouched}
												styles={StyledStepType.countriesSelectStyles}
												onChange={setFieldValue}
												searchFunction={filterCountries}
												aria-label="select-country"
											/>
										</FormikFieldWrapper>
									</Column>
									<Column tablet={1 / 2}>
										<FormikFieldWrapper
											name="receiverName"
											label={`${t('step-info.name')}*`}
											smallLabel
											languageComponent="registerShipment">
											<StyledFormikFastField
												type="text"
												id="receiverName"
												name="receiverName"
												placeholder={t('step-info.name')}
												autoComplete="name"
											/>
										</FormikFieldWrapper>
									</Column>
									<Column tablet={1 / 2}>
										<FormikFieldWrapper
											name="receiverSsn"
											label={t('step-info.ssn')}
											smallLabel
											languageComponent="registerShipment">
											<StyledFormikFastField
												type="text"
												id="receiverSsn"
												name="receiverSsn"
												placeholder={t('step-info.ssn')}
												autoComplete="off"
											/>
										</FormikFieldWrapper>
									</Column>
								</Columns>
								<Columns>
									<Column tablet={1 / 2}>
										<FormikFieldWrapper
											name="receiverAddress"
											label={`${t('step-info.address')}*`}
											smallLabel
											languageComponent="registerShipment">
											<StyledFormikFastField
												type="text"
												id="receiverAddress"
												name="receiverAddress"
												disabled={readOnlyValuesMapper.receiverAddress}
												placeholder={t('step-info.address')}
												autoComplete="address"
											/>
										</FormikFieldWrapper>
									</Column>
									<Column tablet={1 / 2}>
										{values.country.value !== 'IS' ? (
											<FormikFieldWrapper
												name="receiverForeignPostCode"
												label={`${t('step-info.postcode')}*`}
												smallLabel
												languageComponent="registerShipment">
												<StyledFormikFastField
													id="receiverForeignPostCode"
													name="receiverForeignPostCode"
													type="text"
													placeholder={t('step-info.postcode')}
													autoComplete="postal-code"
												/>
											</FormikFieldWrapper>
										) : (
											<FormikFieldWrapper
												name="receiverPostCode"
												label={`${t('step-info.postcode')}*`}
												smallLabel
												languageComponent="registerShipment">
												<FormikSelect
													id="receiverPostCode"
													name="receiverPostCode"
													value={values.receiverPostCode}
													options={postCodes}
													placeholder={t('step-info.postcode')}
													onBlur={setFieldTouched}
													onChange={setFieldValue}
													autocomplete="postal-code"
												/>
											</FormikFieldWrapper>
										)}
									</Column>
								</Columns>
								{values.country.value !== 'IS' && (
									<Columns>
										<Column tablet={1 / 2}>
											<FormikFieldWrapper
												name="receiverForeignAddressExtended"
												label={t('step-info.address-cont')}
												smallLabel
												languageComponent="registerShipment">
												<StyledFormikFastField
													id="receiverForeignAddressExtended"
													name="receiverForeignAddressExtended"
													type="text"
													placeholder={t('step-info.address-cont')}
													autoComplete="address-line2"
												/>
											</FormikFieldWrapper>
										</Column>
										<Column tablet={1 / 2}>
											<FormikFieldWrapper
												name="receiverForeignPlace"
												label={`${t('step-info.place')}*`}
												smallLabel
												languageComponent="registerShipment">
												<StyledFormikFastField
													id="receiverForeignPlace"
													name="receiverForeignPlace"
													type="text"
													placeholder={t('step-info.place')}
													autoComplete="address-level1"
												/>
											</FormikFieldWrapper>
										</Column>
									</Columns>
								)}
								<Columns>
									<Column tablet={1 / 2}>
										<FormikFieldWrapper
											name="receiverEmail"
											label={t('step-info.email')}
											smallLabel
											languageComponent="registerShipment">
											<StyledFormikFastField
												type="text"
												id="receiverEmail"
												name="receiverEmail"
												placeholder={t('step-info.email')}
												autoComplete="email"
											/>
										</FormikFieldWrapper>
									</Column>
									<Column tablet={1 / 2}>
										<FormikFieldWrapper
											name="receiverPhone"
											label={t('step-info.phone')}
											smallLabel
											languageComponent="registerShipment">
											<StyledFormikFastField
												type="phone"
												id="receiverPhone"
												name="receiverPhone"
												placeholder={t('step-info.phone')}
												autoComplete="phone"
											/>
										</FormikFieldWrapper>
									</Column>
								</Columns>
								<Button primary type="submit" isLoading={modalSubmitPending}>
									{t('myShipments:save')}
								</Button>
							</Form>
						</StyledModals.ModalForm>
					)}
				</Formik>
			)}
		</StyledModals.Wrapper>
	);
};

export default RecipientModal;
